import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import ModalShell from './ModalShell';

const OrderConfirmModal = ({ open, onClose, onConfirm, order, isCreating }) => {
  const [showItems, setShowItems] = useState(true);

  const getServiceTypeLabel = (type) => {
    if (type === 'laundry') return 'Стирка';
    if (type === 'dry-cleaning') return 'Химчистка мебели';
    return type || '-';
  };

  const getPickupTypeLabel = (type, serviceType) => {
    if (serviceType === 'dry-cleaning') return 'Визит специалиста';
    if (type === 'scheduled') return 'Доставка от клиента';
    if (type === 'self_delivery') return 'Самостоятельная доставка';
    return type || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth()+1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  const shouldShowDateTime = () => {
    if (order.serviceType === 'dry-cleaning') return order.pickupDate && order.pickupTime;
    return order.pickupType !== 'self_delivery' && order.pickupDate && order.pickupTime;
  };

  const mainItems = (order.items || []).filter(i => !i.isAdditional);
  const serviceItems = (order.items || []).filter(i => i.isAdditional && i.additionalType !== 'discount');
  const discountItems = (order.items || []).filter(i => i.isAdditional && i.additionalType === 'discount');

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      zIndexClass="z-[70]"
      panelClassName="max-w-md flex max-h-[90vh] flex-col"
    >
        {/* Заголовок */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">Подтверждение заказа</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Контент */}
        <div className="overflow-y-auto flex-1 p-4 space-y-3 text-sm">
          {/* Основная инфо */}
          <div className="space-y-2">
            <Row label="Клиент" value={order.clientType === 'organization' ? order.clientName || order.name : 'Физ. лицо'} />
            <Row label="Телефон" value={order.phone} />
            {order.address && <Row label="Адрес" value={order.address} />}
            <Row label="Услуга" value={getServiceTypeLabel(order.serviceType)} />
            <Row label="Тип забора" value={getPickupTypeLabel(order.pickupType, order.serviceType)} />
            {order.assigneeName && <Row label="Исполнитель" value={order.assigneeName} />}
            {shouldShowDateTime() && <>
              <Row label="Дата" value={formatDate(order.pickupDate)} />
              <Row label="Время" value={order.pickupTime} />
            </>}
          </div>

          {/* Корзина */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <button
              onClick={() => setShowItems(v => !v)}
              className="flex items-center justify-between w-full mb-2"
            >
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                Корзина
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {mainItems.length} пред. {serviceItems.length > 0 ? `• ${serviceItems.length} усл.` : ''} {discountItems.length > 0 ? `• ${discountItems.length} скид.` : ''}
                </span>
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showItems ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>

            {showItems && (
              <div className="space-y-1">
                {/* Предметы */}
                {mainItems.map((item, idx) => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-1.5 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-blue-400 font-mono shrink-0">#{idx + 1}</span>
                      <span className="text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                    </div>
                    <span className="text-xs text-gray-500 ml-2 shrink-0">
                      {item.unit === 'кг' && item.weight ? `${item.weight} кг` :
                       (item.unit === 'м²' || item.unit === 'м2') && item.area ? `${item.area} м²` :
                       `${item.quantity || 1} шт.`}
                    </span>
                  </div>
                ))}

                {/* Доп услуги */}
                {serviceItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-1.5 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
                    <span className="text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                    <span className="text-purple-600 dark:text-purple-400 text-xs ml-2 shrink-0">
                      {item.unitType === 'percent' ? `${item.price}%` : `${item.price} ₽`}
                    </span>
                  </div>
                ))}

                {/* Скидки */}
                {discountItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between px-3 py-1.5 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                    <span className="text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                    <span className="text-orange-600 dark:text-orange-400 text-xs ml-2 shrink-0">
                      -{item.unitType === 'percent' ? `${item.price}%` : `${item.price} ₽`}
                    </span>
                  </div>
                ))}

                {order.items?.length === 0 && (
                  <p className="text-gray-400 text-center py-2 text-xs">Корзина пуста</p>
                )}
              </div>
            )}
          </div>

          {order.additionalInfo && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <span className="text-gray-500 dark:text-gray-400 text-xs">Доп. информация:</span>
              <p className="text-gray-900 dark:text-gray-100 mt-1">{order.additionalInfo}</p>
            </div>
          )}
        </div>

        {/* Кнопки */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="flex-1 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={onConfirm}
            disabled={isCreating}
            className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors relative overflow-hidden disabled:opacity-70"
          >
            {isCreating ? (
              <>
                <motion.div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)' }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                />
                Создание...
              </>
            ) : 'Создать заказ'}
          </button>
        </div>
    </ModalShell>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-2">
    <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
    <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{value}</span>
  </div>
);

export default OrderConfirmModal;
